/**
 * DocumentDesigner — shared visual builder UI for Transcript & Certificate templates.
 * Left panel: form controls (header/branding/content/footer/etc.)
 * Right panel: live HTML preview rendered from `config`.
 * Used by:
 *   /admin/designers/transcript
 *   /admin/designers/certificate
 */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { CheckCircle, FloppyDisk, ArrowCounterClockwise, Eye, UploadSimple, Plus, Trash } from "@phosphor-icons/react";
import CertificateCanvasEditor from "../components/CertificateCanvasEditor";

/** Reusable file-picker that uploads via /api/upload and writes the returned
 *  URL back into the designer config via the `onChange` callback. */
function ImageField({ label, value, onChange, hint, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      onChange(url);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div data-testid={testid}>
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2 items-start">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL or upload →" className="rounded-none flex-1" />
        <input type="file" accept="image/*" ref={inputRef} className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
        <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="rounded-none gap-1 text-xs" data-testid={`${testid}-btn`}>
          <UploadSimple size={12} /> {busy ? "…" : "Upload"}
        </Button>
      </div>
      {value && <img src={value} alt={label} className="mt-2 h-12 object-contain border border-slate-200 bg-slate-50 px-2" />}
      {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function deepSet(obj, path, value) {
  const next = JSON.parse(JSON.stringify(obj));
  const parts = path.split(".");
  let ref = next;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!ref[parts[i]]) ref[parts[i]] = {};
    ref = ref[parts[i]];
  }
  ref[parts[parts.length - 1]] = value;
  return next;
}

function TranscriptPreview({ config, sample }) {
  const c = config || {};
  const h = c.header || {};
  const t = c.table || {};
  const f = c.footer || {};
  const blocks = c.custom_blocks || [];
  const before = blocks.filter(b => b.position === "before_table");
  const afterTable = blocks.filter(b => b.position === "after_table");
  const afterFooter = blocks.filter(b => b.position === "after_footer");
  const renderBlock = (b) => (
    <div key={b.id} className="my-4 border-l-4 px-4 py-3 bg-slate-50" style={{ borderColor: h.accent_color || "#c9a227" }}>
      <p className="text-xs uppercase tracking-widest font-bold mb-1" style={{ color: h.primary_color || "#0c1b3a" }}>{b.title}</p>
      <p className="text-sm text-slate-700 whitespace-pre-wrap">{renderPlaceholders(b.body || "", sample)}</p>
    </div>
  );
  return (
    <div className="bg-white shadow-lg" style={{ minHeight: 800 }}>
      <div className="px-8 py-6 text-white" style={{ background: h.primary_color || "#0c1b3a" }}>
        <div className="flex items-center justify-between">
          {h.logo_url ? <img src={h.logo_url} alt="logo" className="h-12" /> : <div className="h-12 w-12 bg-white/20" />}
          <div className={`flex-1 ${h.alignment === "center" ? "text-center" : h.alignment === "right" ? "text-right" : ""}`}>
            <p className="text-xs uppercase tracking-widest font-bold" style={{ color: h.accent_color || "#c9a227" }}>Official Academic Record</p>
            <h1 className="text-2xl font-bold mt-1">{h.school_name || "School Name"}</h1>
          </div>
          <div className="w-12" />
        </div>
      </div>
      <div className="px-8 py-6">
        <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
          {(c.student_info?.fields || []).map(field => (
            <div key={field}>
              <p className="text-xs uppercase tracking-wider text-slate-500">{field.replace(/([A-Z])/g, ' $1').trim()}</p>
              <p className="font-semibold">{sample[field] || `{{${field}}}`}</p>
            </div>
          ))}
        </div>
        {before.map(renderBlock)}
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr style={{ background: t.header_color || "#0c1b3a", color: "white" }}>
              {(t.columns || []).map(col => <th key={col.key} className="text-left px-3 py-2" style={{ width: col.width }}>{col.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {(sample.courses || []).map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 1 ? (t.row_alt_color || "#f5f1e6") : "white" }}>
                {(t.columns || []).map(col => <td key={col.key} className="px-3 py-2 border-b border-slate-100">{row[col.key] != null ? row[col.key] : "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {afterTable.map(renderBlock)}
        <div className="mt-8 grid grid-cols-2 gap-8 text-sm">
          <div className="border-t border-slate-300 pt-2">
            {f.registrar_signature_url ? <img src={f.registrar_signature_url} alt="sig" className="h-12 mb-1" /> : <div className="h-12 mb-1" />}
            <p className="text-xs uppercase tracking-wider text-slate-500">{f.registrar_label || "Registrar"}</p>
          </div>
          <div className="border-t border-slate-300 pt-2">
            {f.director_signature_url ? <img src={f.director_signature_url} alt="sig" className="h-12 mb-1" /> : <div className="h-12 mb-1" />}
            <p className="text-xs uppercase tracking-wider text-slate-500">{f.director_label || "Programme Director"}</p>
          </div>
        </div>
        {f.show_qr && (
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <div className="w-12 h-12 border border-slate-300 grid place-items-center text-[8px]">QR</div>
            <span>{f.verification_label || "Verification #"}: SBL-XXXX-XXXX-XXXX</span>
          </div>
        )}
        {afterFooter.map(renderBlock)}
      </div>
    </div>
  );
}

function CertificatePreview({ config, sample, orientation = "portrait" }) {
  const c = config || {};
  const b = c.background || {};
  const br = c.branding || {};
  const co = c.content || {};
  const blocks = c.custom_blocks || [];
  const before = blocks.filter(x => x.position === "before_body");
  const after = blocks.filter(x => x.position === "after_body");
  const footerBlocks = blocks.filter(x => x.position === "footer");
  const isLandscape = orientation === "landscape";
  const previewSize = isLandscape ? { aspectRatio: "297 / 210", minHeight: 380 } : { aspectRatio: "210 / 297", minHeight: 600 };
  const renderBlock = (x) => (
    <div key={x.id} className="my-3 mx-auto max-w-2xl border border-dashed px-4 py-2" style={{ borderColor: b.border_color || "#c9a227" }}>
      <p className="text-xs uppercase tracking-widest font-bold" style={{ color: b.border_color || "#c9a227" }}>{x.title}</p>
      <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{renderPlaceholders(x.body || "", sample)}</p>
    </div>
  );
  return (
    <div className="bg-white relative" style={{ ...previewSize, border: `${b.border_width || 12}px solid ${b.border_color || "#c9a227"}`, backgroundImage: b.image_url ? `url(${b.image_url})` : "none", backgroundSize: "cover", backgroundPosition: "center" }} data-testid={`cert-preview-${orientation}`}>
      {b.watermark_url && <img src={b.watermark_url} alt="wm" className="absolute inset-0 m-auto opacity-10" style={{ maxWidth: "60%" }} />}
      <div className="relative px-8 py-8 text-center h-full flex flex-col">
        {co.art_url && <img src={co.art_url} alt="art" className="absolute left-1/2 top-2 -translate-x-1/2 h-10 opacity-90 pointer-events-none" />}
        {br.logo_url ? <img src={br.logo_url} alt="logo" className="h-16 mx-auto mb-2" /> : <div className="h-16 mx-auto mb-2 bg-slate-100 w-16" />}
        <h2 className="text-xs uppercase tracking-[0.4em] text-slate-500 font-bold">{br.school_name || "School Name"}</h2>
        <h1 className="text-3xl font-bold mt-4 mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: b.border_color || "#c9a227" }}>{co.title || "Certificate of Completion"}</h1>
        {before.map(renderBlock)}
        <p className="text-base text-slate-700 max-w-2xl mx-auto leading-relaxed mt-6 mb-6">{renderPlaceholders(co.body || "", sample)}</p>
        {after.map(renderBlock)}
        <div className="flex-1" />
        <div className="grid grid-cols-2 gap-8 mt-6 text-sm">
          {(co.signatures || []).map((s, i) => (
            <div key={i} className="border-t border-slate-400 pt-2">
              {s.signature_url ? <img src={s.signature_url} alt="" className="h-10 mx-auto mb-1" /> : <div className="h-10 mb-1" />}
              <p className="text-xs uppercase tracking-wider text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
        {footerBlocks.map(renderBlock)}
        {co.seal_url && <img src={co.seal_url} alt="seal" className="absolute right-6 bottom-6 w-16 h-16 opacity-90" />}
      </div>
    </div>
  );
}

function renderPlaceholders(text, sample) {
  return text.replace(/\{\{(\w+)\}\}/g, (m, key) => sample[key] || m);
}

export default function DocumentDesignerPage() {
  const { kind } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  // Track which kind the current config belongs to. Without this, switching
  // /admin/designers/{kind} would render the new kind's controls against the
  // previous kind's config for one paint, crashing on `config.header.logo_url`
  // (certificate config has no .header).
  const [configKind, setConfigKind] = useState(null);
  const [name, setName] = useState("");
  const [active, setActive] = useState(null);
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);

  const isTranscript = kind === "transcript";
  const isCert = kind === "certificate";

  const sample = isTranscript
    ? {
        StudentName: "Jane Adebayo", StudentID: "SBL-2025-0123",
        ProgrammeName: "Leadership Foundation", GraduationDate: "May 2026",
        CGPA: "3.81",
        Attendance: "97%",
        CapstoneProject: "School Operational Excellence Audit (Distinction)",
        CapstoneTitle: "School Operational Excellence Audit",
        CapstoneScore: "92",
        Classification: "Distinction",
        FinalRemarks: "Outstanding leadership potential demonstrated.",
        CourseName: "Foundations of School Leadership",
        CourseGrade: "B",
        courses: [
          { course_code: "LDR101", course_title: "Foundations of School Leadership", credits: 3, attendance: "98%", score: 88, grade: "B" },
          { course_code: "LDR102", course_title: "Operations Excellence",            credits: 3, attendance: "96%", score: 91, grade: "A" },
          { course_code: "LDR103", course_title: "Strategic Planning",               credits: 3, attendance: "100%", score: 84, grade: "B" },
        ],
      }
    : {
        StudentName: "Jane Adebayo",
        ProgrammeName: "Leadership Foundation",
        CompletionDate: "May 8, 2026",
        CertificateNumber: "SBL-CERT-001234",
        InstructorName: "Dr. Olu Aremu",
        Classification: "Upper Merit",
        Attendance: "98%",
        CourseName: "Foundations of School Leadership",
      };

  useEffect(() => {
    setConfig(null);
    setConfigKind(null);
    const currentKind = kind;
    api.get(`/designs/${currentKind}/active`).then(r => {
      // Guard against races: only commit if the user is still on this kind.
      if (currentKind !== kind) return;
      setConfig(r.data.config);
      setConfigKind(currentKind);
      setActive(r.data);
    }).catch(() => {});
    api.get(`/designs/${currentKind}`).then(r => setVersions(r.data || [])).catch(() => {});
  }, [kind]);

  const refreshVersions = () => api.get(`/designs/${kind}`).then(r => setVersions(r.data || []));
  const update = (path, value) => setConfig(c => deepSet(c, path, value));

  const save = async (asActive) => {
    if (!name.trim()) return toast.error("Give this version a name first");
    setSaving(true);
    try {
      const { data } = await api.post("/designs", { name, kind, config, set_active: asActive });
      toast.success(`Saved as v${data.version}${asActive ? " and set active" : ""}`);
      setName("");
      refreshVersions();
      if (asActive) setActive(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const activate = async (id) => {
    try {
      await api.put(`/designs/${id}/activate`);
      toast.success("Activated");
      refreshVersions();
      api.get(`/designs/${kind}/active`).then(r => { setConfig(r.data.config); setConfigKind(kind); setActive(r.data); });
    } catch { toast.error("Failed"); }
  };

  const loadVersion = (v) => { setConfig(v.config); setConfigKind(kind); setName(v.name + " (copy)"); toast.info(`Loaded ${v.name} v${v.version} — saving will create a new version.`); };

  const deleteVersion = async (v) => {
    if (v.is_active) return toast.error("Set another version active first, then delete this one.");
    if (!window.confirm(`Delete "${v.name} v${v.version}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/designs/${v.id}`);
      toast.success(`Deleted v${v.version}`);
      refreshVersions();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const restoreDefault = async () => {
    const { data } = await api.get(`/designs/${kind}/default`);
    setConfig(data.config);
    setConfigKind(kind);
    toast.info("Default configuration loaded — save to create a new version.");
  };

  // Only render the editor when the loaded config matches the URL kind.
  if (!config || configKind !== kind) return <Layout><div className="p-8">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8" data-testid={`designer-${kind}`}>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Visual Designer</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {isTranscript ? "Transcript Designer" : "Certificate Designer"}
            </h1>
            {active && !active.is_default && <p className="text-xs text-slate-500 mt-1">Active: <b>{active.name} v{active.version}</b></p>}
            <div className="flex gap-1 mt-2">
              <button onClick={() => navigate("/admin/designers/transcript")} data-testid="switch-to-transcript" className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border ${isTranscript ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-600"}`}>Transcript</button>
              <button onClick={() => navigate("/admin/designers/certificate")} data-testid="switch-to-certificate" className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border ${isCert ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-600"}`}>Certificate</button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={restoreDefault} className="rounded-none gap-1 text-xs"><ArrowCounterClockwise size={12} /> Load default</Button>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Version name…" className="rounded-none w-48" data-testid="design-name-input" />
            <Button variant="outline" disabled={saving} onClick={() => save(false)} className="rounded-none gap-1" data-testid="save-draft-btn"><FloppyDisk size={14} weight="bold" /> Save Version</Button>
            <Button disabled={saving} onClick={() => save(true)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-1" data-testid="save-active-btn"><CheckCircle size={14} weight="fill" /> Save & Set Active</Button>
          </div>
        </div>

        {/* Round 45 · v2 — mode + orientation toggle (certificate only).
             Restores the classic Template editor as default; Free Canvas is
             now opt-in. Orientation defaults to portrait to match legacy. */}
        {isCert && (
          <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="cert-mode-bar">
            <span className="text-xs uppercase tracking-widest text-slate-500 font-bold mr-1">Editor</span>
            <button
              onClick={() => update("editor_mode", "template")}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border ${((config.editor_mode || "template") === "template") ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-600"}`}
              data-testid="mode-template-btn"
            >
              Template
            </button>
            <button
              onClick={() => update("editor_mode", "canvas")}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border ${(config.editor_mode === "canvas") ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-600"}`}
              data-testid="mode-canvas-btn"
            >
              Free Canvas
            </button>
            <span className="mx-3 text-slate-300">|</span>
            <span className="text-xs uppercase tracking-widest text-slate-500 font-bold mr-1">Orientation</span>
            <button
              onClick={() => update("orientation", "portrait")}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border ${((config.orientation || "portrait") === "portrait") ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-600"}`}
              data-testid="orientation-portrait-btn"
            >
              Portrait
            </button>
            <button
              onClick={() => update("orientation", "landscape")}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border ${(config.orientation === "landscape") ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-600"}`}
              data-testid="orientation-landscape-btn"
            >
              Landscape
            </button>
          </div>
        )}

        {isCert && config.editor_mode === "canvas" ? (
          <div className="space-y-4" data-testid="designer-canvas-wrapper">
            <CertificateCanvasEditor
              initialElements={config.elements || []}
              onChange={(els) => update("elements", els)}
              sample={sample}
              orientation={config.orientation || "portrait"}
              background={config.canvas_background || {}}
              onBackgroundChange={(bg) => update("canvas_background", bg)}
              onTemplateApply={(t) => {
                setConfig(c => ({
                  ...c,
                  elements: t.build(),
                  canvas_background: t.canvas_background || {},
                  orientation: t.orientation || c.orientation || "portrait",
                  editor_mode: "canvas",
                }));
              }}
            />
            <Card className="rounded-none border border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Version History</p>
                {versions.length === 0 && <p className="text-xs text-slate-400">No saved versions yet.</p>}
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {versions.map(v => (
                    <div key={v.id} className={`flex items-center justify-between p-2 border ${v.is_active ? "border-blue-700 bg-blue-50" : "border-slate-200"}`}>
                      <div>
                        <p className="text-xs font-semibold">v{v.version} · {v.name}</p>
                        <p className="text-[10px] text-slate-500">{new Date(v.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-1">
                        {!v.is_active && <Button size="sm" onClick={() => activate(v.id)} className="rounded-none text-xs h-7" data-testid={`activate-version-${v.version}`}>Set Active</Button>}
                        <Button size="sm" variant="outline" onClick={() => loadVersion(v)} className="rounded-none text-xs h-7" title="Load into editor (as copy)"><Eye size={12} /></Button>
                        {!v.is_active && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => deleteVersion(v)}
                            className="rounded-none text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                            title="Delete this version"
                            data-testid={`delete-version-${v.version}`}
                          ><Trash size={12} /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Controls */}
          <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-2" data-testid="designer-controls">
            {isTranscript && <TranscriptControls config={config} update={update} />}
            {isCert && <CertificateControls config={config} update={update} />}

            <Card className="rounded-none border border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Version History</p>
                {versions.length === 0 && <p className="text-xs text-slate-400">No saved versions yet.</p>}
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {versions.map(v => (
                    <div key={v.id} className={`flex items-center justify-between p-2 border ${v.is_active ? "border-blue-700 bg-blue-50" : "border-slate-200"}`}>
                      <div>
                        <p className="text-xs font-semibold">v{v.version} · {v.name}</p>
                        <p className="text-[10px] text-slate-500">{new Date(v.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-1">
                        {!v.is_active && <Button size="sm" onClick={() => activate(v.id)} className="rounded-none text-xs h-7" data-testid={`activate-version-${v.version}`}>Set Active</Button>}
                        <Button size="sm" variant="outline" onClick={() => loadVersion(v)} className="rounded-none text-xs h-7" title="Load into editor (as copy)"><Eye size={12} /></Button>
                        {!v.is_active && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => deleteVersion(v)}
                            className="rounded-none text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                            title="Delete this version"
                            data-testid={`delete-version-${v.version}`}
                          ><Trash size={12} /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview */}
          <div className="lg:sticky lg:top-4 lg:h-fit" data-testid="designer-preview">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Live Preview</p>
            {isTranscript && <TranscriptPreview config={config} sample={sample} />}
            {isCert && <CertificatePreview config={config} sample={sample} orientation={config.orientation || "portrait"} />}
            <p className="text-xs text-slate-500 mt-3">Placeholders available: {(config.placeholders_available || []).join(", ")}</p>
          </div>
        </div>
        )}
      </div>
    </Layout>
  );
}

function TranscriptControls({ config, update }) {
  const addBlock = () => {
    const blocks = [...(config.custom_blocks || []), { id: Date.now().toString(), title: "New Section", body: "Custom content with {{placeholders}}…", position: "after_table" }];
    update("custom_blocks", blocks);
  };
  const removeBlock = (id) => update("custom_blocks", (config.custom_blocks || []).filter(b => b.id !== id));
  const updateBlock = (id, field, value) => update("custom_blocks", (config.custom_blocks || []).map(b => b.id === id ? { ...b, [field]: value } : b));
  return (
    <>
      <Card className="rounded-none border border-slate-200"><CardContent className="p-4 space-y-3">
        <p className="text-sm font-bold uppercase tracking-wider text-slate-700">Header</p>
        <ImageField label="School Logo" value={config.header.logo_url} onChange={v => update("header.logo_url", v)} testid="tr-logo" />
        <div><Label className="text-xs">School Name</Label><Input value={config.header.school_name || ""} onChange={e => update("header.school_name", e.target.value)} className="rounded-none" /></div>
        <div><Label className="text-xs">Programme Label</Label><Input value={config.header.programme_label || ""} onChange={e => update("header.programme_label", e.target.value)} className="rounded-none" /></div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-xs">Alignment</Label>
            <select value={config.header.alignment || "center"} onChange={e => update("header.alignment", e.target.value)} className="w-full h-9 border border-slate-200 rounded-none px-2 text-sm">
              {["left", "center", "right"].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Primary</Label><Input type="color" value={config.header.primary_color || "#0c1b3a"} onChange={e => update("header.primary_color", e.target.value)} className="rounded-none p-1 h-9" /></div>
          <div><Label className="text-xs">Accent</Label><Input type="color" value={config.header.accent_color || "#c9a227"} onChange={e => update("header.accent_color", e.target.value)} className="rounded-none p-1 h-9" /></div>
        </div>
      </CardContent></Card>

      <Card className="rounded-none border border-slate-200"><CardContent className="p-4 space-y-2">
        <p className="text-sm font-bold uppercase tracking-wider text-slate-700">Student Info Fields</p>
        <p className="text-xs text-slate-500">Comma-separated list of placeholder keys (e.g. StudentName, Attendance, CapstoneProject, CourseName).</p>
        <Input value={(config.student_info?.fields || []).join(", ")} onChange={e => update("student_info.fields", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} className="rounded-none" />
      </CardContent></Card>

      <Card className="rounded-none border border-slate-200"><CardContent className="p-4 space-y-3">
        <p className="text-sm font-bold uppercase tracking-wider text-slate-700">Academic Table</p>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Header Colour</Label><Input type="color" value={config.table?.header_color || "#0c1b3a"} onChange={e => update("table.header_color", e.target.value)} className="rounded-none p-1 h-9" /></div>
          <div><Label className="text-xs">Alt Row Colour</Label><Input type="color" value={config.table?.row_alt_color || "#f5f1e6"} onChange={e => update("table.row_alt_color", e.target.value)} className="rounded-none p-1 h-9" /></div>
        </div>
        <div>
          <Label className="text-xs">Columns (JSON list: key/label/width). Suggested keys: course_code, course_title (=Course Name), credits, attendance, score, grade.</Label>
          <Textarea value={JSON.stringify(config.table?.columns || [], null, 2)} onChange={e => { try { update("table.columns", JSON.parse(e.target.value)); } catch { /* ignore */ } }} className="rounded-none font-mono text-xs" rows={6} />
        </div>
      </CardContent></Card>

      <Card className="rounded-none border border-slate-200"><CardContent className="p-4 space-y-3">
        <p className="text-sm font-bold uppercase tracking-wider text-slate-700">Footer</p>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Registrar Label</Label><Input value={config.footer?.registrar_label || ""} onChange={e => update("footer.registrar_label", e.target.value)} className="rounded-none" /></div>
          <div><Label className="text-xs">Director Label</Label><Input value={config.footer?.director_label || ""} onChange={e => update("footer.director_label", e.target.value)} className="rounded-none" /></div>
        </div>
        <ImageField label="Registrar Signature" value={config.footer?.registrar_signature_url} onChange={v => update("footer.registrar_signature_url", v)} testid="tr-sig-registrar" />
        <ImageField label="Director Signature" value={config.footer?.director_signature_url} onChange={v => update("footer.director_signature_url", v)} testid="tr-sig-director" />
        <div className="flex items-center gap-2"><input type="checkbox" id="show_qr" checked={!!config.footer?.show_qr} onChange={e => update("footer.show_qr", e.target.checked)} /><Label htmlFor="show_qr" className="text-xs">Show verification QR</Label></div>
        <div><Label className="text-xs">Verification Label</Label><Input value={config.footer?.verification_label || ""} onChange={e => update("footer.verification_label", e.target.value)} className="rounded-none" /></div>
      </CardContent></Card>

      <Card className="rounded-none border border-slate-200"><CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold uppercase tracking-wider text-slate-700">Custom Boxes / Headers</p>
          <Button type="button" variant="outline" onClick={addBlock} className="rounded-none gap-1 text-xs" data-testid="tr-add-block"><Plus size={12} /> Add box</Button>
        </div>
        <p className="text-xs text-slate-500">Use these for free-form sections like Capstone summary, Honours list, Examiner notes etc. Position them around the academic table.</p>
        {(config.custom_blocks || []).length === 0 && <p className="text-xs text-slate-400">No custom blocks yet.</p>}
        {(config.custom_blocks || []).map(b => (
          <div key={b.id} className="border border-slate-200 p-3 space-y-2" data-testid={`tr-block-${b.id}`}>
            <div className="grid grid-cols-3 gap-2">
              <Input value={b.title} onChange={e => updateBlock(b.id, "title", e.target.value)} placeholder="Box title" className="rounded-none col-span-2 text-sm" />
              <select value={b.position} onChange={e => updateBlock(b.id, "position", e.target.value)} className="border border-slate-200 rounded-none px-2 text-xs">
                <option value="before_table">Before table</option>
                <option value="after_table">After table</option>
                <option value="after_footer">After footer</option>
              </select>
            </div>
            <Textarea value={b.body} onChange={e => updateBlock(b.id, "body", e.target.value)} rows={3} className="rounded-none text-sm" placeholder="Box body — placeholders allowed" />
            <button type="button" onClick={() => removeBlock(b.id)} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"><Trash size={12} /> Remove</button>
          </div>
        ))}
      </CardContent></Card>
    </>
  );
}

function CertificateControls({ config, update }) {
  const addBlock = () => {
    const blocks = [...(config.custom_blocks || []), { id: Date.now().toString(), title: "Additional Section", body: "Custom content…", position: "after_body" }];
    update("custom_blocks", blocks);
  };
  const removeBlock = (id) => update("custom_blocks", (config.custom_blocks || []).filter(b => b.id !== id));
  const updateBlock = (id, field, value) => update("custom_blocks", (config.custom_blocks || []).map(b => b.id === id ? { ...b, [field]: value } : b));
  const sigs = config.content?.signatures || [];
  const updateSig = (i, field, v) => {
    const next = sigs.map((s, idx) => idx === i ? { ...s, [field]: v } : s);
    update("content.signatures", next);
  };
  return (
    <>
      <Card className="rounded-none border border-slate-200"><CardContent className="p-4 space-y-3">
        <p className="text-sm font-bold uppercase tracking-wider text-slate-700">Background</p>
        <ImageField label="Background Image" value={config.background?.image_url} onChange={v => update("background.image_url", v)} testid="cert-bg" />
        <ImageField label="Watermark" value={config.background?.watermark_url} onChange={v => update("background.watermark_url", v)} testid="cert-watermark" />
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Border Colour</Label><Input type="color" value={config.background?.border_color || "#c9a227"} onChange={e => update("background.border_color", e.target.value)} className="rounded-none p-1 h-9" /></div>
          <div><Label className="text-xs">Border Width (px)</Label><Input type="number" value={config.background?.border_width || 12} onChange={e => update("background.border_width", parseInt(e.target.value, 10) || 0)} className="rounded-none" /></div>
        </div>
      </CardContent></Card>

      <Card className="rounded-none border border-slate-200"><CardContent className="p-4 space-y-3">
        <p className="text-sm font-bold uppercase tracking-wider text-slate-700">Branding</p>
        <ImageField label="Logo" value={config.branding?.logo_url} onChange={v => update("branding.logo_url", v)} testid="cert-logo" />
        <div><Label className="text-xs">School Name</Label><Input value={config.branding?.school_name || ""} onChange={e => update("branding.school_name", e.target.value)} className="rounded-none" /></div>
        <div><Label className="text-xs">Programme Label</Label><Input value={config.branding?.programme_label || ""} onChange={e => update("branding.programme_label", e.target.value)} className="rounded-none" /></div>
      </CardContent></Card>

      <Card className="rounded-none border border-slate-200"><CardContent className="p-4 space-y-3">
        <p className="text-sm font-bold uppercase tracking-wider text-slate-700">Content</p>
        <div><Label className="text-xs">Certificate Title</Label><Input value={config.content?.title || ""} onChange={e => update("content.title", e.target.value)} className="rounded-none" /></div>
        <div><Label className="text-xs">Body Text (use placeholders)</Label><Textarea value={config.content?.body || ""} onChange={e => update("content.body", e.target.value)} className="rounded-none" rows={5} /></div>
        <ImageField label="Seal / Foil Stamp" value={config.content?.seal_url} onChange={v => update("content.seal_url", v)} hint="Appears bottom-right. Use a transparent PNG (max 5MB)." testid="cert-seal" />
        <ImageField label="Decorative Art / Ribbon" value={config.content?.art_url} onChange={v => update("content.art_url", v)} hint="Optional flourish appearing at top-centre." testid="cert-art" />
        <div className="flex items-center gap-2"><input type="checkbox" id="cert_qr" checked={!!config.content?.show_qr} onChange={e => update("content.show_qr", e.target.checked)} /><Label htmlFor="cert_qr" className="text-xs">Show verification QR</Label></div>
      </CardContent></Card>

      <Card className="rounded-none border border-slate-200"><CardContent className="p-4 space-y-3">
        <p className="text-sm font-bold uppercase tracking-wider text-slate-700">Signatures</p>
        {sigs.map((s, i) => (
          <div key={i} className="border border-slate-200 p-3 space-y-2" data-testid={`cert-sig-${i}`}>
            <Input value={s.label || ""} onChange={e => updateSig(i, "label", e.target.value)} placeholder="Label (e.g. Registrar)" className="rounded-none text-sm" />
            <ImageField label="Signature image" value={s.signature_url} onChange={v => updateSig(i, "signature_url", v)} testid={`cert-sig-img-${i}`} />
          </div>
        ))}
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={() => update("content.signatures", [...sigs, { label: "Signatory", signature_url: "" }])} className="rounded-none text-xs gap-1" data-testid="cert-sig-add"><Plus size={12} /> Add signature</Button>
          {sigs.length > 0 && <Button type="button" variant="outline" onClick={() => update("content.signatures", sigs.slice(0, -1))} className="rounded-none text-xs gap-1"><Trash size={12} /> Remove last</Button>}
        </div>
      </CardContent></Card>

      <Card className="rounded-none border border-slate-200"><CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold uppercase tracking-wider text-slate-700">Custom Boxes / Headers</p>
          <Button type="button" variant="outline" onClick={addBlock} className="rounded-none gap-1 text-xs" data-testid="cert-add-block"><Plus size={12} /> Add box</Button>
        </div>
        <p className="text-xs text-slate-500">Add free-form decorative or content boxes anywhere on the certificate.</p>
        {(config.custom_blocks || []).length === 0 && <p className="text-xs text-slate-400">No custom blocks yet.</p>}
        {(config.custom_blocks || []).map(b => (
          <div key={b.id} className="border border-slate-200 p-3 space-y-2" data-testid={`cert-block-${b.id}`}>
            <div className="grid grid-cols-3 gap-2">
              <Input value={b.title} onChange={e => updateBlock(b.id, "title", e.target.value)} placeholder="Box title" className="rounded-none col-span-2 text-sm" />
              <select value={b.position} onChange={e => updateBlock(b.id, "position", e.target.value)} className="border border-slate-200 rounded-none px-2 text-xs">
                <option value="before_body">Before body</option>
                <option value="after_body">After body</option>
                <option value="footer">Footer</option>
              </select>
            </div>
            <Textarea value={b.body} onChange={e => updateBlock(b.id, "body", e.target.value)} rows={3} className="rounded-none text-sm" placeholder="Box body — placeholders allowed" />
            <button type="button" onClick={() => removeBlock(b.id)} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"><Trash size={12} /> Remove</button>
          </div>
        ))}
      </CardContent></Card>
    </>
  );
}
